import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { BookOpen, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { priceBookService, priceBookItemService, profileService } from "@/services";

interface PriceBook {
  id: string;
  name: string;
  status_id: string | null;
  current_status: { id: string; name: string } | null;
  vendor: { name: string } | null;
  effective_date: string;
}


export interface PriceBookItem {
  id: string;
  sequence: number;
  itemcode: string;
  description: string;
  price: number;
  discount: number;
  unit: string | null;
  is_active: boolean;
}

interface Props {
  selectedPriceBookId: string | null;
  onSelectPriceBook: (id: string) => void;
  onPriceBookItemsLoaded?: (items: PriceBookItem[]) => void;
}

export function PriceBookSelector({ selectedPriceBookId, onSelectPriceBook, onPriceBookItemsLoaded }: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();

  // Fetch user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const result = await profileService.getCurrentProfile();
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Fetch price books using service
  const { data: priceBooks, isLoading: loadingPriceBooks } = useQuery({
    queryKey: ["price-books-for-validation", profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await priceBookService.listForSelector(profile.company_id);
      if (result.error) throw result.error;
      return result.data as PriceBook[];
    },
    enabled: !!profile?.company_id,
  });

  // Fetch price book items for selected price book using service
  const { data: priceBookItems, isLoading: loadingItems } = useQuery({
    queryKey: ["price-book-items-for-validation", selectedPriceBookId],
    queryFn: async () => {
      if (!selectedPriceBookId) return [];
      const result = await priceBookItemService.listByPriceBook(selectedPriceBookId);
      if (result.error) throw result.error;
      return result.data.map(item => ({
        id: item.id,
        sequence: item.sequence,
        itemcode: item.itemcode,
        description: item.description,
        price: item.price,
        discount: item.discount,
        unit: item.unit,
        is_active: item.is_active,
      })) as PriceBookItem[];
    },
    enabled: !!selectedPriceBookId,
  });

  // Notify parent when price book items are loaded
  useEffect(() => {
    if (priceBookItems && onPriceBookItemsLoaded) {
      onPriceBookItemsLoaded(priceBookItems);
    }
  }, [priceBookItems, onPriceBookItemsLoaded]);

  const filteredItems = priceBookItems?.filter(
    (item) =>
      item.itemcode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const calculateDiscountedPrice = (price: number, discount: number) => {
    return price * (1 - discount / 100);
  };

  const selectedPriceBook = priceBooks?.find((pb) => pb.id === selectedPriceBookId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          Select Price Book for Validation
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="flex border-t">
          {/* Price Book List - Narrow Column */}
          <div className="w-64 border-r">
            <ScrollArea className="h-[350px]">
              {loadingPriceBooks ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : priceBooks?.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No price books found
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {priceBooks?.map((pb) => (
                    <button
                      key={pb.id}
                      onClick={() => onSelectPriceBook(pb.id)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg transition-colors",
                        selectedPriceBookId === pb.id
                          ? "bg-primary/10 border border-primary/20"
                          : "hover:bg-muted"
                      )}
                    >
                      <p className="font-medium text-sm truncate">{pb.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {pb.vendor?.name || "No vendor"}
                      </p>
                      <div className="mt-1">
                        <Badge
                          variant={pb.current_status?.name?.toLowerCase() === "active" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {pb.current_status?.name || 'Unknown'}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Price Book Items - Main Area */}
          <div className="flex-1">
            {selectedPriceBookId ? (
              <div className="h-[350px] flex flex-col">
                {/* Header with search */}
                <div className="p-3 border-b flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-sm">{selectedPriceBook?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {priceBookItems?.length || 0} items
                    </p>
                  </div>
                  <div className="relative w-48">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 h-9 text-sm"
                    />
                  </div>
                </div>

                {/* Items Table */}
                <ScrollArea className="flex-1">
                  {loadingItems ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20 text-xs">Code</TableHead>
                          <TableHead className="text-xs">Description</TableHead>
                          <TableHead className="text-right w-20 text-xs">Price</TableHead>
                          <TableHead className="text-right w-16 text-xs">Disc %</TableHead>
                          <TableHead className="text-right w-20 text-xs">Net</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredItems?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                              {searchQuery ? "No matching items" : "No items in this price book"}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredItems?.map((item) => (
                            <TableRow key={item.id} className={!item.is_active ? "opacity-50" : ""}>
                              <TableCell className="font-mono text-xs">{item.itemcode}</TableCell>
                              <TableCell className="text-sm">{item.description}</TableCell>
                              <TableCell className="text-right text-sm">
                                {formatCurrency(item.price)}
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {item.discount > 0 ? `${item.discount}%` : "-"}
                              </TableCell>
                              <TableCell className="text-right font-medium text-sm">
                                {formatCurrency(calculateDiscountedPrice(item.price, item.discount))}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  )}
                </ScrollArea>
              </div>
            ) : (
              <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select a price book to view items</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
